import {
  useOrderConfiguration,
  type MaterialAndShadeCatalog,
} from "@jasper/use-cases";
import {
  Badge,
  Card,
  Divider,
  LoadingOverlay,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { Stack, Title } from "@mantine/core";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { IconCheck, IconCpu, IconPlus, IconTruck } from "@tabler/icons-react";
import { Group } from "@mantine/core";
import { Button } from "@mantine/core";
import { ProductIcon } from "@jasper/forms";
import { useProductDescription } from "@jasper/product-workflows";
import { DeliveryCarrierEnum } from "@jasper/client-models";

const CarrierLabels = {
  [DeliveryCarrierEnum.FEDEX_TNT]: "FedEx TNT",
  [DeliveryCarrierEnum.BIP]: "BIP",
  [DeliveryCarrierEnum.CIBLEX]: "Ciblex",
  [DeliveryCarrierEnum.UPS]: "UPS",
  [DeliveryCarrierEnum.STERNE]: "Sterne",
  [DeliveryCarrierEnum.CHRONOPOST]: "Chronopost",
} as const;

export const OrderConfirmationTitle = () => {
  return (
    <Title variant="display" size="md">
      Commande{" "}
      <Text inherit span variant="accent">
        envoyée
      </Text>
      .
    </Title>
  );
};

type OrderProduct = NonNullable<
  ReturnType<typeof useOrderConfiguration>["order"]
>["products"][number];

const ProductLine = ({
  product,
  materialAndShadeCatalog,
  isLast,
}: {
  product: OrderProduct;
  materialAndShadeCatalog: MaterialAndShadeCatalog;
  isLast?: boolean;
}) => {
  const { describeProduct } = useProductDescription();
  const { headline, lines } = describeProduct(product, materialAndShadeCatalog);

  return (
    <Stack gap="s4" pb={isLast ? undefined : "s4"}>
      <Divider />
      <Group justify="space-between" wrap="nowrap">
        <Group gap="s4" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon size={36} radius="md" variant="light" color="brand">
            <ProductIcon productTypes={[product.type]} />
          </ThemeIcon>
          <Stack gap="s1" style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} truncate>
              {headline}
            </Text>
            {lines.map((line, index) => (
              <Text
                variant="caption"
                c="ink.3"
                truncate
                key={`${index}-${line}`}
              >
                {line}
              </Text>
            ))}
          </Stack>
        </Group>
        {/* TODO: When we have Fullfilment/Dispatch implemented, we could display a real status (E.g: Try-in=Delivered, PAP=in conception)  */}
        <Badge color="accent" style={{ flexShrink: 0 }}>
          En conception
        </Badge>
      </Group>
    </Stack>
  );
};

export const OrderConfirmation = () => {
  const { getProductsCountLabel } = useProductDescription();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { order, materialAndShadeCatalog, isLoading, error, isEditable } =
    useOrderConfiguration({
      orderId: orderId!,
      fetchPolicy: "network-only",
    });

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  if (isEditable) {
    return <Navigate to={`..`} replace />;
  }

  // If there is an error, we don't want to show an error message as the error is only about displaying the confirmation page
  // Showing an error might be interpreted as a failure of the order confirmation.
  if (error || !order || !materialAndShadeCatalog) {
    return <Navigate to={`/order/${orderId}`} replace />;
  }

  return (
    <Stack w="100%" h="100%" align="center">
      <Stack align="center" gap="s6" my="s16" maw={800}>
        <ThemeIcon color="success.1" c="success.6" radius="full" w={60} h={60}>
          <IconCheck size="100%" />
        </ThemeIcon>

        <Stack align="center" gap="s5" w="100%">
          <Stack align="center" gap="s1">
            <OrderConfirmationTitle />
            <Text>
              Réf.{" "}
              <Text span inherit fw={600}>
                {order.reference}
              </Text>{" "}
              · {getProductsCountLabel(order.products.length)} pour{" "}
              {order.patient?.firstName} {order.patient?.lastName}
            </Text>
          </Stack>

          <Card w="100%">
            <Stack gap="s4">
              <Stack gap="s1">
                <Group gap="s2">
                  <IconTruck color="var(--mantine-color-brand-5)" size={18} />
                  <Text fw={600}>Livraison estimée au cabinet</Text>
                </Group>
                <Stack gap="s1">
                  <Title c="brand.6" variant="display" size="l">
                    {new Date(order.deliveryDate).toLocaleDateString("fr-FR", {
                      month: "long",
                      day: "numeric",
                    })}
                  </Title>
                  {/* TODO: Add real provider */}
                  {order.dentalOffice.deliveryCarrier && (
                    <Text variant="caption" tt="capitalize">
                      Acheminement{" "}
                      {CarrierLabels[order.dentalOffice.deliveryCarrier]}
                    </Text>
                  )}
                </Stack>
                <Divider />
              </Stack>
              <Group gap="s2">
                <IconCpu color="var(--mantine-color-brand-5)" size={18} />
                <Text variant="caption">
                  Prise en charge démarrée · conception au laboratoire
                </Text>
              </Group>
            </Stack>
          </Card>

          <Card w="100%">
            <Stack gap="s4">
              <Stack gap="s4">
                <Text variant="eyebrow" c="ink">
                  Dans cette commande · {order.products.length}
                </Text>
                <Stack gap="s1">
                  {order.products.map((product, index) => (
                    <ProductLine
                      key={product.id}
                      product={product}
                      materialAndShadeCatalog={materialAndShadeCatalog}
                      isLast={index === order.products.length - 1}
                    />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Card>

          <Button
            w="100%"
            size="lg"
            onClick={() => navigate(`/order/${orderId}`)}
          >
            Voir ma commande
          </Button>
          <Button
            w="100%"
            size="lg"
            variant="default"
            leftSection={<IconPlus size={18} />}
            onClick={() => navigate("/orders/new")}
          >
            Nouvelle commande
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
